import { useMemo, useState } from 'react'
import { useMutation } from 'react-query'
import { Link } from 'react-router-dom'
import parse from 'html-react-parser'
import styled from 'styled-components'
import { t } from 'ttag'
import { useDispatch } from 'react-redux'

import { Select } from '@h2/components/inputs/select'
import { Button } from '@h2/components/buttons/button'
import { Field } from '@h2/components/inputs/field'
import { HtmlEditor } from '@h2/components/inputs/htmlEditor'
import { useForm } from '@h2/common/components/hooks/useForm'
import { showUserFeedback } from '@h2/common/actions/showUserFeedback'
import { Flex } from '@h2/components/layout/flex'
import { Text } from '@h2/components/typography/text'
import { api } from '@h2/api'

const JournalLink = styled(Link)`
  font-weight: bold;
`
const JournalListItem = styled.li`
  list-style: none;
  margin: 0;
  padding: 0;
`
const EntryListItem = styled.li`
  p:last-child {
    margin-bottom: 0;
  }
  list-style: none;
`
const ListContainer = styled.ul`
  margin: 0 0 15px 0;
  padding: 0 0 0 15px;
`
const JournalPopup = ({ journal, objectType, objectId, title, onComplete }) => {
  const reduxDispatch = useDispatch()
  const [isFormOpen, setFormOpen] = useState(!journal.entries.length)
  const { register, handleOnSubmit, error, isInvalid, dispatch } = useForm({
    journalId(form) {
      if (!form.journalId) {
        return t`You must specify a journal to add the entry to`
      }
    }
  })
  const journalSelectItems = useMemo(() => {
    return journal.journals?.map(jrnl => ({
      value: jrnl.id,
      label: jrnl.name
    }))
  }, [journal.journals])
  const closeForm = () => {
    const payload = {
      type: 'update',
      payload: {
        entry: ''
      }
    }

    dispatch(payload)
    setFormOpen(false)
  }
  const { mutate, isLoading: isCreatingEntry } = useMutation(
    form => {
      const { journalId, entry } = form

      return api.fetch(
        journalId ? `/journals/${journalId}/createEntry` : '/journals/createEntry',
        {
          method: 'POST',
          version: 'v1',
          body: JSON.stringify({
            referencedObjectId: objectId,
            referencedObjectFlagId: objectType,
            referencedObjectName: title,
            entry
          })
        }
      )
    },
    {
      onSuccess: frm => {
        onComplete(frm)
        closeForm()
      },
      onError: () => {
        reduxDispatch(showUserFeedback(t`Could not create journal`, 'error'))
      }
    }
  )
  const formContent = () => {
    if (!isFormOpen) {
      return (
        <>
          <Button onClick={() => setFormOpen(true)}>{t`Add New Entry`}</Button>
        </>
      )
    }

    return (
      <>
        <form onSubmit={handleOnSubmit(form => mutate(form))}>
          <Field
            isRequired
            label={t`Enter a new entry in the box below`}
            desc={error.journalId}
            status={error.journalId ? 'error' : 'default'}>
            <Select
              id="journalId"
              items={journalSelectItems}
              {...register('journalId', {
                parser: val => val?.value
              })}
            />
          </Field>
          <Field
            isRequired
            label={t`Enter a new entry in the box below`}
            desc={error.entry}
            status={error.entry ? 'error' : 'default'}>
            <HtmlEditor {...register('entry')} />
          </Field>
          <Flex gap="15px" style={{ padding: '15px 0' }}>
            <Button
              size="large"
              type="submit"
              isLoading={isCreatingEntry}
              isDisabled={isCreatingEntry || isInvalid}>
              {t`Create Journal Entry`}
            </Button>
            <Button display="red" type="button" onClick={closeForm}>
              {t`Cancel`}
            </Button>
          </Flex>
        </form>
      </>
    )
  }
  const journalContent = () => {
    if (isFormOpen) {
      return null
    }

    const usedJournalIds = [...new Set(journal.entries.map(jrnl => jrnl.journalId))]

    return (
      <ListContainer>
        {journal.journals
          .filter(jrnl => usedJournalIds.includes(jrnl.id))
          .map(jrnl => (
            <JournalListItem key={jrnl.id}>
              <JournalLink to={jrnl.url}>{jrnl.name}</JournalLink>
              <ListContainer>
                {journal.entries
                  .filter(entry => entry.journalId === jrnl.id)
                  .map(entry => (
                    <EntryListItem key={`entry-${entry.id}`}>
                      <Flex>
                        <Text as="span" style={{ width: '15%', flexGrow: 1 }}>
                          {new Date(entry.dateEntered).toLocaleDateString()}
                        </Text>
                        <Text as="span" style={{ width: '100%' }}>
                          {parse(entry.entry)}
                        </Text>
                      </Flex>
                    </EntryListItem>
                  ))}
              </ListContainer>
            </JournalListItem>
          ))}
      </ListContainer>
    )
  }

  return (
    <div>
      {journalContent()}
      {formContent()}
    </div>
  )
}

export { JournalPopup }
